import { Outlet } from 'react-router-dom';
import { useState } from 'react';
import { useUserStore } from '@/store/useUserStore';
import { SessionActiveModal } from './SessionActiveModal';

// Public "action" pages are meant to be opened while signed in (e.g. an admin
// approving an exam voucher from an email link), so they must never be
// interrupted by the session-active modal.
const SESSION_MODAL_EXCLUDED_PREFIXES = ['/exam-voucher/action', '/addon-invoice/action'];

/**
 * PublicRoute Component
 *
 * Wraps the login + password/registration flows. Authentication is not hard
 * required here (unauthenticated users need these pages), but when a user with
 * an ACTIVE session lands on one of them we surface a "session active" modal so
 * they can continue into the app or explicitly log out to switch accounts —
 * rather than being shown a login form that appears to do nothing.
 */
export const PublicRoute = () => {
  // Decide ONCE, at mount, whether to show the modal. Reading a live/reactive
  // value here would flash the modal for a split second when a user submits the
  // login form (which sets the token, then navigates away). The snapshot avoids
  // that: a freshly-authenticated form login was `false` at mount.
  const [showSessionModal] = useState(() => {
    const { token, user } = useUserStore.getState();
    if (!token || !user) return false;

    const path = window.location.pathname;
    if (SESSION_MODAL_EXCLUDED_PREFIXES.some((prefix) => path.startsWith(prefix))) {
      return false;
    }

    // The login page runs its own, more specific "wrong user for this invite"
    // modal when a token-based join redirect is pending — defer to it.
    const pendingRedirect = sessionStorage.getItem('redirectAfterLogin') || '';
    if (pendingRedirect.includes('token=')) return false;

    return true;
  });

  return (
    <>
      <Outlet />
      {showSessionModal && <SessionActiveModal />}
    </>
  );
};
